/**
 * Central SEO configuration for lareinabeautysalonllc.com.
 *
 * The two IDs below are the ONLY values that need to be filled in by the salon
 * owner. Everything else (meta tags, Open Graph, JSON-LD, hreflang) is derived
 * automatically and injected at runtime by <Seo />.
 *
 *  1. GOOGLE_SEARCH_CONSOLE_TOKEN
 *     Search Console → Add property → "lareinabeautysalonllc.com" →
 *     HTML tag method → copy ONLY the value of the `content` attribute.
 *
 *  2. GA4_MEASUREMENT_ID
 *     Google Analytics → Admin → Data streams → Web → "G-XXXXXXXXXX".
 */

import { salon } from './content'
import type { Lang } from '../contexts/LanguageContext'

/** Paste the Search Console verification token here (leave '' to skip). */
export const GOOGLE_SEARCH_CONSOLE_TOKEN = ''

/** Paste the GA4 Measurement ID here, e.g. 'G-XXXXXXXXXX' (leave '' to skip). */
export const GA4_MEASUREMENT_ID = ''

export const SITE_URL = 'https://lareinabeautysalonllc.com'

export const BUSINESS_NAME = 'La Reina Beauty Salon LLC'

type Meta = {
  title: string
  description: string
  keywords: string
  locale: string
}

export const seoByLang: Record<Lang, Meta> = {
  es: {
    title:
      'Dominican Salon Staten Island | Salón Dominicano | La Reina Beauty Salon',
    description:
      'Salón dominicano en Staten Island, New York. Especialistas en blowout dominicano, color, tratamientos premium y extensiones 100% humano.',
    keywords:
      'dominican salon, dominican salon Staten Island, salón dominicano Staten Island, hair salon Staten Island, salón de belleza Staten Island, blowout dominicano, color, extensiones, tratamientos cabello',
    locale: 'es_US',
  },
  en: {
    title:
      'Dominican Salon Staten Island NY | Hair Salon | La Reina Beauty Salon',
    description:
      'Dominican salon in Staten Island, New York. Specialists in the Dominican blowout, hair color, premium treatments and 100% human hair extensions.',
    keywords:
      'dominican salon, dominican salon Staten Island, salón dominicano Staten Island, hair salon Staten Island NY, beauty salon Staten Island, dominican blowout, hair color, hair extensions, keratin treatment',
    locale: 'en_US',
  },
}

export const OG_IMAGE =
  'https://cdn.magicpatterns.com/uploads/tjg1P1rNcTJUp7t798vWds/BANNER_LA_REINA.png'

export const LOGO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/wsW8vTGWRQQdAGuQxLakY3/LOGO_LA_REINA.png'

/**
 * schema.org LocalBusiness / HairSalon structured data.
 * Google uses this for the knowledge panel, rich results and local pack.
 */
export function buildLocalBusinessSchema(lang: Lang) {
  return {
    '@context': 'https://schema.org',
    '@type': ['HairSalon', 'BeautySalon'],
    '@id': `${SITE_URL}/#business`,
    name: BUSINESS_NAME,
    alternateName: [
      'La Reina Beauty Salon',
      'La Reina Dominican Salon',
      'Dominican Salon Staten Island',
      'Salón Dominicano Staten Island',
    ],
    keywords:
      'dominican salon, dominican salon Staten Island, salón dominicano Staten Island, hair salon Staten Island',
    knowsAbout: [
      'Dominican salon',
      'Dominican blowout',
      'Hair color',
      'Keratin treatments',
      'Human hair extensions',
    ],
    url: SITE_URL,
    image: [OG_IMAGE, LOGO_IMAGE],
    logo: LOGO_IMAGE,
    description: seoByLang[lang].description,
    telephone: '+1-718-273-3434',
    email: salon.email,
    priceRange: '$$',
    currenciesAccepted: 'USD',
    paymentAccepted: 'Cash, Credit Card, Debit Card',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2031 Forest Avenue',
      addressLocality: 'Staten Island',
      addressRegion: 'NY',
      postalCode: '10303',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 40.6284,
      longitude: -74.1483,
    },
    hasMap: salon.mapsHref,
    areaServed: [
      { '@type': 'City', name: 'Staten Island' },
      { '@type': 'AdministrativeArea', name: 'New York' },
    ],
    sameAs: [salon.instagramHref, salon.tiktokHref],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
        opens: '09:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Friday',
        opens: '09:00',
        closes: '19:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '08:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Sunday',
        opens: '00:00',
        closes: '00:00',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.7',
      bestRating: '5',
      worstRating: '1',
      reviewCount: '5',
    },
    review: [
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Yudelki V.' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'CCMedia Mogul' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Aaliyah L.' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Melissa B.' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Alexis C.' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      },
    ],
    makesOffer: [
      'Dominican Blowout',
      'Hair Cut',
      'Blow Dry',
      'Hair Color',
      'Balayage',
      'Keratin Treatment',
      'Human Hair Extensions',
    ].map((name) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name },
    })),
  }
}

/** Website + search-action schema, helps Google understand the single page. */
export function buildWebSiteSchema(lang: Lang) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: BUSINESS_NAME,
    inLanguage: lang === 'es' ? 'es-US' : 'en-US',
    description: seoByLang[lang].description,
    publisher: { '@id': `${SITE_URL}/#business` },
  }
}
